import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IXP_TEMPLATES, LOCATIONS, PROVIDER_TEMPLATES } from '@hosting-sim/core';
import { Button, Notice } from '../components/ui';
import { useAuth } from '../store/auth';
import { AuthStage, GlassField, GlassLink, GlassPanel } from '../components/layout/AuthStage';
import { ResendForm } from './Account';

/**
 * Startseite: Titelbild und Anmeldung.
 *
 * Bühne, Glasfläche und Eingabefelder liegen in `AuthStage` – dieselbe
 * Gestaltung tragen auch Bestätigung und Passwortwechsel. Hier steht nur noch,
 * was die Anmeldung ausmacht.
 *
 * Gestaltungsidee ist die Leitwarte eines Netzbetreibers, nicht die
 * Anmeldemaske eines Dienstes. Das Titelbild füllt die Bühne, darüber liegt
 * eine Glasfläche in der Mitte – so, wie im Spiel ein Fenster über der Netzkarte
 * liegt. Die Fachsprache der Simulation trägt die Details: Konsolenkopf mit
 * Statuspunkt, Beschriftungen in Versalien, Kennzahlen dicktengleich.
 *
 * Drei Entscheidungen, die man kennen sollte:
 *
 *  - Das Bild liegt formatfüllend (`object-cover`) statt vollständig sichtbar.
 *    Der Anschnitt ist nach oben gezogen, damit der Titel im Bild erhalten
 *    bleibt. Auf schmalen Schirmen wird er dennoch beschnitten – deshalb trägt
 *    der Seitenkopf die Marke unabhängig vom Bild.
 *  - Die Eingabefelder sind hier eigens ausgezeichnet und benutzen nicht
 *    `Input` aus der Komponentensammlung. Deren Felder haben eine deckende
 *    Fläche, die die Glaswirkung zunichtemachen würde. Diese Seite ist die
 *    einzige mit Glas; die Sammlung deswegen umzubauen wäre der falsche Weg.
 *  - Helles und dunkles Schema bleiben erhalten. Das Bild ist in beiden Fällen
 *    dunkel, die Glasfläche wechselt zwischen getönt und milchig.
 */

/**
 * Der Umfang des Standortkatalogs.
 *
 * Bewusst aus den Daten gezählt und nicht abgeschrieben: Wer den Katalog
 * erweitert, ändert damit auch diese Zeile. Eine gepflegte Zahl wäre nach dem
 * ersten neuen Standort falsch.
 */
const CATALOG_FACTS = [
  { value: LOCATIONS.length, label: 'Standorte' },
  { value: IXP_TEMPLATES.length, label: 'Internetknoten' },
  { value: PROVIDER_TEMPLATES.length, label: 'Netze' },
];

export function Login() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const { login, register, busy, error, pendingVerification, unverifiedEmail, clearPending } = useAuth();
  const navigate = useNavigate();

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    // `register` meldet nicht an, sondern setzt `pendingVerification` – erst der
    // Link aus der E-Mail schaltet das Konto frei.
    const ok = mode === 'login' ? await login(email, password) : await register(email, password, displayName);
    if (ok) navigate('/');
  };

  /*
   * Nach der Registrierung tritt die Anmeldemaske beiseite. Sie stehen zu
   * lassen und nur einen Hinweis darüberzusetzen wäre irreführend: Ein zweiter
   * Versuch mit denselben Daten liefe in "Adresse bereits registriert".
   */
  const backLink = (
    <div className="border-t border-black/10 bg-black/[0.04] px-5 py-2.5 text-center dark:border-white/10 dark:bg-black/25">
      <button
        type="button"
        onClick={clearPending}
        className="text-xs text-panel-600 underline-offset-2 transition-colors hover:text-panel-900 hover:underline dark:text-white/45 dark:hover:text-white"
      >
        Zurück zur Anmeldung
      </button>
    </div>
  );

  if (unverifiedEmail) {
    return (
      <AuthStage>
        <GlassPanel title="Postfach" footer={backLink}>
          <Notice tone="warning">
            Diese Adresse wurde noch nicht bestätigt. Sieh in deinem Postfach nach oder fordere unten eine neue
            Mail an.
          </Notice>
          <ResendForm initialEmail={unverifiedEmail} />
        </GlassPanel>
      </AuthStage>
    );
  }

  if (pendingVerification) {
    return (
      <AuthStage>
        <GlassPanel title="Postfach" footer={backLink}>
          <Notice tone="success">{pendingVerification.message}</Notice>
          <p className="text-sm text-panel-700 dark:text-white/70">
            Geschickt an <span className="font-medium text-panel-900 dark:text-white">{pendingVerification.email}</span>.
            Der Link gilt 24 Stunden.
          </p>
          <p className="text-xs text-panel-600 dark:text-white/45">
            Nichts angekommen? Sieh im Spam-Ordner nach. Steht dort ein Tippfehler in der Adresse, lege das
            Konto einfach neu an – unbestätigte Konten werden nach sieben Tagen entfernt.
          </p>
          <ResendForm />
        </GlassPanel>
      </AuthStage>
    );
  }

  return (
    <AuthStage anchor="hero">
      <GlassPanel
        title={mode === 'login' ? 'Anmelden' : 'Registrieren'}
        onSubmit={submit}
        footer={
          /*
            Statusleiste am Fuß der Konsole: der Umfang der Simulation in drei
            Zahlen. Sie stammen aus dem Katalog selbst – siehe CATALOG_FACTS –
            und sagen mehr über das Spiel als ein Werbesatz.
          */
          <div className="grid grid-cols-3 divide-x divide-black/10 border-t border-black/10 bg-black/[0.04] text-center dark:divide-white/10 dark:border-white/10 dark:bg-black/25">
            {CATALOG_FACTS.map((fact) => (
              <div key={fact.label} className="px-2 py-2">
                <p className="tabular font-mono text-base font-semibold leading-none text-panel-900 dark:text-white">{fact.value}</p>
                <p className="mt-1 font-mono text-2xs uppercase tracking-[0.14em] text-panel-600 dark:text-white/40">{fact.label}</p>
              </div>
            ))}
          </div>
        }
      >
        {/* Umschalter */}
        <div className="flex gap-1 rounded-lg border border-black/10 bg-black/[0.05] p-1 dark:border-white/10 dark:bg-black/25">
          {(['login', 'register'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={mode === value}
              className={[
                'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                mode === value
                  ? 'bg-white text-panel-900 shadow-sm dark:bg-white/[0.14] dark:text-white'
                  : 'text-panel-600 hover:text-panel-900 dark:text-white/50 dark:hover:text-white/80',
              ].join(' ')}
            >
              {value === 'login' ? 'Anmelden' : 'Konto anlegen'}
            </button>
          ))}
        </div>

        {mode === 'register' && (
          <GlassField label="Name" value={displayName} onChange={setDisplayName} required placeholder="Miriam Kessler" autoComplete="name" />
        )}
        <GlassField
          label="E-Mail"
          type="email"
          value={email}
          onChange={setEmail}
          required
          autoComplete="email"
          placeholder="admin@example.de"
        />
        <GlassField
          label="Passwort"
          type="password"
          value={password}
          onChange={setPassword}
          required
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          hint={mode === 'register' ? 'Mindestens 10 Zeichen.' : undefined}
        />

        {error && <Notice tone="danger">{error}</Notice>}

        <Button type="submit" variant="primary" loading={busy} className="w-full">
          {mode === 'login' ? 'Anmelden' : 'Konto anlegen'}
        </Button>

        {mode === 'login' && (
          <p className="text-center">
            <GlassLink to="/passwort-vergessen">Passwort vergessen?</GlassLink>
          </p>
        )}
      </GlassPanel>
    </AuthStage>
  );
}
